import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type MappingPayload, type MappingRow, type MappingGate, type MappingStatus } from '../api/client';

// Symbol → Device → Part mapping review checkpoint (§5.5). The AI proposes a
// device type per symbol (memory-first, taxonomy fallback); the estimator
// confirms/overrides. Symbols still needing review are flagged red and BLOCK BOM
// generation until resolved. Confirmed choices are remembered across projects.
export function MappingReview() {
  const { id = '' } = useParams();
  const [data, setData] = useState<MappingPayload | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setData(await api.getMappings(id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load mappings'); }
  }
  useEffect(() => { void load(); }, [id]);

  function patch(symbolId: string, change: Partial<MappingRow>, gate?: MappingGate) {
    setData((d) => d && {
      ...d,
      mappings: d.mappings.map((m) => m.symbol_id === symbolId ? { ...m, ...change } : m),
      gate: gate ?? d.gate,
    });
  }

  async function setDevice(m: MappingRow, device_key: string) {
    const dev = data?.devices.find((x) => x.key === device_key);
    patch(m.symbol_id, { device_key, device_type: dev?.label ?? null, assembly_hint: dev?.assemblyHint ?? null, status: 'confirmed', source: 'manual' });
    try { const r = await api.updateMapping(id, m.symbol_id, { device_key }); patch(m.symbol_id, {}, r.gate); }
    catch { void load(); }
  }

  async function setStatus(m: MappingRow, status: MappingStatus) {
    patch(m.symbol_id, { status });
    try { const r = await api.updateMapping(id, m.symbol_id, { status }); patch(m.symbol_id, {}, r.gate); }
    catch { void load(); }
  }

  async function repropose() {
    setBusy(true);
    try { await api.proposeMappings(id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Re-propose failed'); }
    finally { setBusy(false); }
  }

  const gate = data?.gate;
  const rows = data?.mappings ?? [];

  return (
    <div>
      <Link to={`/projects/${id}`} className="muted" style={{ fontSize: 13 }}>← Scope Analysis</Link>
      <h1 style={{ margin: '4px 0 0' }}>Symbol → Device → Part mapping</h1>
      <p className="muted" style={{ marginTop: 4 }}>
        The AI proposes a device type for each legend symbol. Confirm or override every row — this is a
        reliability checkpoint. <strong>It maps types; it never counts.</strong>
      </p>

      {/* BOM gate */}
      {gate && (
        <div className={`banner ${gate.blocks_bom ? 'banner-danger' : ''}`} style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            {gate.blocks_bom
              ? <><strong>{gate.needs_review} symbol{gate.needs_review === 1 ? '' : 's'} need review.</strong> BOM generation is blocked until every symbol is resolved.</>
              : <><strong>All {gate.total} symbols resolved.</strong> Ready to generate the Count Sheet and BOM.</>}
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {gate.confirmed} confirmed · {gate.proposed} proposed (unconfirmed) · {gate.needs_review} needs review · {gate.excluded} excluded
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={repropose} disabled={busy}>{busy ? 'Re-proposing…' : 'Re-propose'}</button>
        </div>
      )}
      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        {!rows.length && <p className="muted">No symbols yet. Upload and analyze a plan set first.</p>}
        {rows.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Tag</th><th>Legend description</th><th>System</th>
                <th>Proposed device type</th><th>Suggested part family</th>
                <th>Conf.</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.symbol_id} style={rowStyle(m.status)}>
                  <td><strong>{m.symbol_tag ?? '—'}</strong></td>
                  <td>{m.legend_description ?? '—'}</td>
                  <td className="muted">{m.system_name ?? '—'}</td>
                  <td>
                    <select value={m.device_key ?? ''} onChange={(e) => setDevice(m, e.target.value)} style={{ minWidth: 200 }}>
                      <option value="" disabled>— select device —</option>
                      {(data?.devices ?? []).map((d) => (
                        <option key={d.key} value={d.key}>{d.label} · {d.system}</option>
                      ))}
                    </select>
                  </td>
                  <td className="muted" style={{ fontSize: 12, maxWidth: 280 }}>{m.assembly_hint ?? '—'}</td>
                  <td>{m.confidence ? `${Math.round(m.confidence * 100)}%` : '—'}</td>
                  <td><span className={`pill pill-${m.status}`}>{m.status.replace('_', ' ')}</span>
                    {m.source === 'memory' && <div className="muted" style={{ fontSize: 11 }}>from memory</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {m.status !== 'confirmed' && m.device_key && (
                      <button className="btn btn-ghost btn-sm" onClick={() => setStatus(m, 'confirmed')}>Confirm</button>
                    )}
                    {m.status !== 'excluded'
                      ? <button className="btn btn-ghost btn-sm" onClick={() => setStatus(m, 'excluded')} title="Not in this scope">Exclude</button>
                      : <button className="btn btn-ghost btn-sm" onClick={() => setStatus(m, 'needs_review')}>Restore</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function rowStyle(status: string): React.CSSProperties {
  if (status === 'needs_review') return { background: 'rgba(197,16,16,0.06)' };
  if (status === 'confirmed') return { background: 'rgba(14,124,123,0.06)' };
  if (status === 'excluded') return { opacity: 0.5 };
  return {};
}
